/**
 * Phase 3.2 — Bypass-refusal reason translation.
 *
 * The backend's refer_visit response surfaces machine-readable reason
 * codes via `bypass_reasons_blocking`. The SendVisitDialog and any
 * future surfaces translate them through this map so we don't repeat
 * the same strings everywhere.
 *
 * Codes are stable; new codes can be added without breaking existing
 * UI. Unknown codes fall back to a humanised version of the slug.
 */

export const REFERRAL_REASON_TEXT = {
  no_target_doctor: 'No destination doctor selected',
  payment_due: 'Payment due at this hop',
  open_awaiting_payment: 'Procedure payment is currently parked with you',
  consultation_fee_pending: 'Consultation fee not yet collected',
};

export const translateReferralReason = (code) => {
  if (!code) return null;
  if (REFERRAL_REASON_TEXT[code]) return REFERRAL_REASON_TEXT[code];
  // Unknown code → make it readable. "some_new_reason" → "Some new reason".
  const cleaned = String(code).replace(/_/g, ' ').trim();
  if (!cleaned) return null;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

// Shared post-send clarification that sets the new handover expectation:
// the sender no longer sees the visit in their live queue (it's a clean
// transfer of care, not a coworked visit), but it still appears under
// their daily dashboard as forwarded so they retain credit for the work.
const HANDOVER_NOTE = (
  "You won't see this in your live queue anymore — it shows under your "
  + "daily dashboard as forwarded. Open it from the patient's history "
  + "if you need to check what happens next."
);

export const summariseReferralOutcome = ({
  pathTaken,
  reasons,
  targetDoctorName,
}) => {
  const target = targetDoctorName ? `Dr ${targetDoctorName}` : 'the target doctor';
  if (pathTaken === 'bypass') {
    return {
      tone: 'success',
      headline: `Sent directly to ${target}'s queue`,
      detail: null,
      note: HANDOVER_NOTE,
    };
  }
  const translated = (reasons || [])
    .map(translateReferralReason)
    .filter(Boolean);
  const detail = translated.length
    ? translated.join(' · ')
    : null;
  return {
    tone: 'info',
    headline: `Routed via Receptionist — ${target} will pick up next`,
    detail,
    note: HANDOVER_NOTE,
  };
};
